import { Log } from '@apparel/core/log';
import { I18n } from '@components/Trans';
import { classList } from '@lib/utils';
import Chip from '@material-ui/core/Chip';
import { OverrideProps } from '@material-ui/core/OverridableComponent';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Tab, { TabProps } from '@material-ui/core/Tab';
import MUITabs, { TabsProps } from '@material-ui/core/Tabs';
import { call } from '@packages/functions';
import { createMemo } from '@packages/react';
import { TabEventHandler, TabObject } from '@packages/tabs/lib/TabContainer';
import { Map } from 'immutable';
import { debounce } from 'lodash';
import React, { Fragment, isValidElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Context } from './context';

export interface TabsComponentTypeMap<P = Record<string, any>, D extends React.ElementType = any> {
    props: P & {
        component?: any;
        debug?: boolean;
        onChange?: TabEventHandler;
        onMount?: (index: number, tab: TabObject) => void;
        tabValidationData?: { [key: string]: any } | Map<string, any>;

        orientation?: 'vertical' | 'horizontal';
        fullWidth?: boolean;

        variant?: TabsProps['variant'];

        tabProps?: TabProps & { component: any };
        tabsProps?: TabsProps;
    };
    defaultComponent: D;
    classKey: '';
}

type TabsComponentProps<
    D extends React.ElementType = TabsComponentTypeMap['defaultComponent'],
    P = Record<string, any>,
> = OverrideProps<TabsComponentTypeMap<P, D>, D>;

/**
 * @param props
 * @returns {*}
 * @constructor
 */
export const Tabs = createMemo<TabsComponentProps>(
    (props) => {
        const {
            debug: debug$,

            orientation,
            variant,
            fullWidth,
            component,
            onChange,
            onMount,
            tabProps = {} as TabProps & { component: any },
            tabsProps = {} as TabsProps,
            tabValidationData,
            ...rest
        } = props;

        const classes = useStyles();

        const log = Log.channel('packages/tabs/Tabs').setActive(!!debug$);

        const RootComponent = component || Fragment;
        const [state, actions] = useContext(Context);

        const [validations, setValidations] = useState({});

        const tabs$ = state.get('tabs');
        const index = state.get('index');
        const url = state.get('url');
        const showTabs = state.get('showTabs');
        const tabChips = state.get('tabChips') || {};
        const tabChipsStyle = state.get('tabChipsStyle') || null;

        /**
         * Change tab event
         * @param event
         * @param idx
         */
        const handleChange = useCallback(
            (event, idx) => {
                if (idx === index) {
                    return;
                }

                actions.navigate(idx);

                call(onChange, event, idx, tabs$[index], tabs$);
            },
            [tabs$, onChange, index],
        );

        useEffect(() => {
            // Pass current index & tab to onMount event
            call(onMount, index, tabs$[index]);
        }, [tabs$, url]);

        const isMap = Map.isMap(tabValidationData) && tabValidationData instanceof Map;
        const tabVData = isMap ? (tabValidationData as Map<string, any>).toJS() : tabValidationData;
        const tabVDataHash = isMap ? (tabValidationData as Map<string, any>).hashCode() : tabValidationData;

        // Log validation helper
        const logValidations = useMemo(
            () => debounce(() => log.debug('Set validations', { validations }), 100),
            [validations],
        );

        const handleValidation = useCallback(
            (valid, tab) => {
                validations[tab.name] = valid;
                setValidations({ ...validations });
                logValidations();
            },
            [validations],
        );

        useEffect(() => {
            log.debug('Validation data changed, run validation', { data: tabVData, hash: tabVDataHash });

            tabs$.forEach((tab) => {
                if (typeof tab.validation === 'function') {
                    handleValidation(tab.validation(tabVData, tab, tabValidationData), tab);
                } else if (tab.validation && typeof tab.validation === 'object') {
                    if (typeof tab.validation.isValid === 'function') {
                        const req = tab.validation.isValid(tabVData);

                        // noinspection SuspiciousTypeOfGuard
                        if (req instanceof Promise) {
                            req.then((valid) => handleValidation(valid, tab));
                        } else {
                            handleValidation(tab.validation.isValid, tab);
                        }
                    }
                }
            });
        }, [tabVDataHash]);

        tabsProps.classes = tabsProps.classes || {};
        tabsProps.classes.root = classList('tabs__container', tabsProps.classes.root);
        tabsProps.classes.scrollable = classList('tabs--scrollable', tabsProps.classes.scrollable);
        tabsProps.classes.flexContainer = classList('tabs__flex-container', tabsProps.classes.flexContainer);
        tabsProps.classes.indicator = classList('tabs__indicator', tabsProps.classes.indicator);

        tabProps.classes = tabProps.classes || {};
        tabProps.classes.root = classList('tabs__tab', tabProps.classes.root);
        tabProps.classes.wrapper = classList('tabs__tab__label-container', tabProps.classes.wrapper);
        tabProps.classes.wrapped = classList('tabs__tab__label-wrapped', tabProps.classes.wrapped);
        tabProps.classes.labelIcon = classList('tabs__tab__label-icon', tabProps.classes.labelIcon);
        tabProps.classes.selected = classList('tabs__tab--selected', tabProps.classes.selected);

        const TabComponent = tabProps.component;

        const tabsList$ = useMemo(
            () =>
                tabs$.map((tab, id) => {
                    const { name, validation, label, props, index } = tab;
                    if (showTabs && typeof showTabs === 'function' && !showTabs(tab)) {
                        return null;
                    }

                    let chip = 0;
                    if (Object.keys(tabChips).includes(`${name}`) && tabChips[`${name}`] > 0) {
                        chip = tabChips[`${name}`];
                    }

                    return (
                        <Tab
                            key={`${id}`}
                            value={index || id}
                            textColor={'primary'}
                            disabled={!validation ? false : !validations[name]}
                            label={
                                TabComponent ? (
                                    isValidElement(TabComponent) ? (
                                        TabComponent
                                    ) : (
                                        <TabComponent
                                            {...{
                                                label,
                                                name,
                                                index,
                                                selected: `${index}` === `${state.get('index')}`,
                                                disabled: !validation ? false : !validations[name],
                                                tabProps,
                                            }}
                                        />
                                    )
                                ) : chip > 0 ? (
                                    <I18n className={'tab__label'} variant={'subtitle2'} style={{ color: 'inherit' }}>
                                        {label}
                                        {tabChipsStyle === 'parentheses' ? (
                                            <span className={classes.customChip}>({chip})</span>
                                        ) : (
                                            <Chip label={chip} size="small" className={classes.chip} />
                                        )}
                                    </I18n>
                                ) : (
                                    <I18n className={'tab__label'} variant={'subtitle2'} style={{ color: 'inherit' }}>
                                        {label}
                                    </I18n>
                                )
                            }
                            {...{
                                ...(tabProps || {}),
                                ...(props || {}),
                                component: undefined,
                            }}
                        />
                    );
                }),
            [tabs$, showTabs, tabProps, index, TabComponent, validations, tabChips],
        );

        if (!(tabs$.length > 1)) {
            return null;
        }

        tabsProps.value = index;

        // Create handle will change the index in useTabs hook
        // this function will pass onChange event and call it underneath.
        return (
            <RootComponent {...rest}>
                <MUITabs
                    indicatorColor={'primary'}
                    textColor={'primary'}
                    onChange={handleChange}
                    variant={fullWidth ? 'fullWidth' : variant || 'standard'}
                    {...tabsProps}
                >
                    {tabsList$}
                </MUITabs>
            </RootComponent>
        );
    },
    {
        displayName: 'Tabs',
    },
);

const useStyles = makeStyles(() => ({
    chip: {
        color: '#fff',
        backgroundColor: '#D22D35',
        marginLeft: '6px',
        fontWeight: 700,
        fontSize: '12px',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        borderRadius: '4px',
    },
    customChip: {
        fontWeight: 400,
        fontSize: '14px',
        paddingLeft: '2px',
    },
}));
